import React, { useEffect, useMemo, useRef, useState } from "react";
import "../styleSheets/selectRecipients.css";
import { Recipient } from "../API";
import { useTranslation } from "react-i18next";

interface SelectRecipientsProps {
    isOpen: boolean;
    setIsOpen: (open: boolean) => void;
    userRecipients: Recipient[];
    selectedRecipients: Recipient[];
    onAddRecipient: (recipient: Recipient) => void;
}

const SelectRecipients: React.FC<SelectRecipientsProps> = ({
                                                               isOpen,
                                                               setIsOpen,
                                                               userRecipients,
                                                               selectedRecipients,
                                                               onAddRecipient,
                                                           }) => {
    const wrapRef = useRef<HTMLDivElement | null>(null);
    const searchInputRef = useRef<HTMLInputElement | null>(null);
    const previousActiveElementRef = useRef<HTMLElement | null>(null);
    const [search, setSearch] = useState("");
    const { t } = useTranslation("common");

    // Close on outside click
    useEffect(() => {
        if (!isOpen) return;

        previousActiveElementRef.current =
            document.activeElement instanceof HTMLElement
                ? document.activeElement
                : null;

        const frameId = window.requestAnimationFrame(() => {
            searchInputRef.current?.focus();
        });

        const onMouseDown = (e: MouseEvent) => {
            const el = wrapRef.current;
            if (!el) return;
            if (!el.contains(e.target as Node)) {
                setIsOpen(false);
            }
        };

        document.addEventListener("mousedown", onMouseDown);
        return () => {
            window.cancelAnimationFrame(frameId);
            document.removeEventListener("mousedown", onMouseDown);

            const previous = previousActiveElementRef.current;
            previousActiveElementRef.current = null;

            if (previous && document.contains(previous)) {
                previous.focus();
            }
        };
    }, [isOpen, setIsOpen]);

    // Reset search when opening/closing
    useEffect(() => {
        if (!isOpen) setSearch("");
    }, [isOpen]);

    const selectedIds = useMemo(() => new Set(selectedRecipients.map((r) => r.id)), [selectedRecipients]);

    const availableRecipients = useMemo(() => {
        const q = search.trim().toLowerCase();

        return userRecipients
            .filter((r) => !selectedIds.has(r.id))
            .filter((r) => {
                if (!q) return true;
                const name = `${r.firstName ?? ""} ${r.lastName ?? ""}`.trim().toLowerCase();
                const email = ((r as any)?.email ?? "").toString().toLowerCase();
                return name.includes(q) || email.includes(q);
            })
            .sort((a, b) => {
                const an = `${a.firstName ?? ""} ${a.lastName ?? ""}`.trim();
                const bn = `${b.firstName ?? ""} ${b.lastName ?? ""}`.trim();
                return an.localeCompare(bn);
            });
    }, [userRecipients, selectedIds, search]);

    if (!isOpen) return null;

    const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
        if (event.key === "Escape") {
            event.preventDefault();
            setIsOpen(false);
        }
    };

    return (
        <div
            className="recipients-dropdown"
            ref={wrapRef}
            role="region"
            aria-label={t("recipient.recipients")}
            onKeyDown={handleKeyDown}
        >
            <div className="recipients-dropdown__searchRow">
                <input
                    ref={searchInputRef}
                    className="recipients-dropdown__search"
                    type="text"
                    value={search}
                    placeholder={t("common.searchRecipient")}
                    aria-label={t("common.searchRecipient")}
                    onChange={(e) => setSearch(e.target.value)}
                />
            </div>

            <div className="recipients-dropdown__list" aria-label={t("recipient.recipients")}>
                {availableRecipients.length === 0 ? (
                    <div className="recipients-dropdown__empty">No available recipients</div>
                ) : (
                    availableRecipients.map((r) => {
                        const label = `${r.firstName ?? ""} ${r.lastName ?? ""}`.trim() || (r as any)?.email || r.id;

                        return (
                            <button
                                type="button"
                                key={r.id}
                                className="recipients-dropdown__item"
                                onClick={() => {
                                    onAddRecipient(r);
                                    setIsOpen(false);
                                }}
                            >
                                {label}
                            </button>
                        );
                    })
                )}
            </div>
        </div>
    );
};

export default SelectRecipients;
